import { createFileRoute, Link } from "@tanstack/react-router";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";
import { PageHero, ServicePageBody } from "@/components/site/PageShell";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { BUSINESS } from "@/components/site/constants";
import { ResponsivePicture } from "@/components/site/images";
import { LECANTO_METAL_BEFORE } from "@/components/site/project-images";
import { Phone, CalendarCheck } from "lucide-react";

const FAQS: { q: string; a: string }[] = [
  {
    q: "Does a roof leak mean I need a new roof?",
    a: "No. An isolated pipe boot, flashing problem, damaged shingle, failed metal fastener, or separated flat-roof seam may be repairable. Replacement becomes more likely when damage or deterioration is widespread.",
  },
  {
    q: "Are roof inspections free?",
    a: "Yes. Destination Roofing provides free roof inspections and does not charge an inspection fee.",
  },
  {
    q: "Who performs the inspection?",
    a: "Owner and licensed roofing contractor Deston Judy generally performs roof-repair inspections personally and gets onto the roof when conditions allow safe access.",
  },
  {
    q: "How quickly can you inspect a leaking roof?",
    a: "Destination Roofing aims to respond promptly, often within approximately one hour. Same-day or next-day appointments are commonly available depending on weather, scheduling, roof safety, and location.",
  },
  {
    q: "Can you find a leak that only appears during heavy rain?",
    a: "Often, but intermittent leaks can be difficult to reproduce. Destination Roofing evaluates the roof surface, flashing, penetrations, valleys, vents, and interior conditions to identify the most likely entry point.",
  },
  {
    q: "Can a metal roof be repaired instead of replaced?",
    a: "Yes, when the panels remain serviceable and corrosion is not widespread. Some metal roofs may qualify for fastener replacement, resealing, or a complete re-screw instead of full replacement.",
  },
  {
    q: "What is a metal-roof re-screw?",
    a: "Existing screws are removed and replaced with wider-diameter long-life fasteners that properly bite into the existing openings. Penetrations and vulnerable details are also resealed as needed.",
  },
  {
    q: "Can you repair a flat or TPO roof?",
    a: "Many TPO seam, flashing, and penetration failures can be repaired when the surrounding roof remains serviceable. Persistent ponding water or widespread deterioration may require a larger correction.",
  },
  {
    q: "Will repaired shingles match the existing roof?",
    a: "Destination Roofing attempts to use the closest compatible material available, but exact color matching may not be possible because existing shingles fade and older products may be discontinued.",
  },
  {
    q: "What warranty comes with a roof repair?",
    a: "Most Destination Roofing repair work includes a five-year workmanship warranty on the area directly repaired. Exact coverage is controlled by the written estimate, contract, and warranty.",
  },
  {
    q: "Will insurance pay for my roof repair?",
    a: "Coverage depends on the cause of damage, policy terms, exclusions, deductible, documentation, and the insurer's decision. Destination Roofing can photograph visible damage and provide a roofing estimate but cannot guarantee coverage.",
  },
  {
    q: "What happens if you discover rotten wood?",
    a: "Destination Roofing photographs damaged decking or wood, explains the condition to the homeowner, and discusses available repair options before proceeding with additional work according to the approved contract.",
  },
  {
    q: "Do you repair roofs outside Citrus County?",
    a: "Roof repairs are primarily performed within Citrus County and nearby areas. Availability outside the county depends on the location, repair size, schedule, and whether the project is practical.",
  },
  {
    q: "Can I try a repair before replacing the roof?",
    a: "Sometimes. When another contractor believes a repair is practical, a homeowner may choose to try it. Destination Roofing will explain honestly when it believes the repair is unlikely to solve the underlying deterioration.",
  },
];

const REVIEWS = [
  {
    name: "Travis (Local Guide)",
    text: "Thanks for your help and knowledge in my roof situation. He came out on a holiday to fix my leak, it was an easy fix he didn't even charge me and that says enough about this business. Thank you! Highly recommend.",
  },
  {
    name: "Andrea Smith",
    text: "Destination was one of three roofing companies called for a small roof leak. First guy said we needed plywood etc. Destin was honest and fixed the actual problem.",
  },
  {
    name: "John Pollette",
    text: "Destin came out after other roofing companies came out and found the leak that no one else could find. He is super professional.",
  },
];

export const Route = createFileRoute("/roof-repair")({
  head: () => ({
    meta: [
      { title: "Roof Repair in Citrus County, FL | Destination Roofing" },
      { name: "description", content: "Roof repair in Citrus County for leaks, shingles, metal roofs, flashing, storm damage, and flat roofs. Free owner-performed inspection. Call (352) 901-3094." },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Roof Repair in Citrus County, FL | Destination Roofing" },
      { property: "og:description", content: "Roof repair in Citrus County for leaks, shingles, metal roofs, flashing, storm damage, and flat roofs. Free owner-performed inspection." },
      { property: "og:url", content: canonical("/roof-repair") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: canonical("/roof-repair") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify([
        breadcrumbListJsonLd([{ label: "Services", href: "/services" }, { label: "Roof Repair", href: "/roof-repair" }]),
        {
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Roof Repair",
          serviceType: "Roof Repair",
          description: "Roof leak repair, missing shingles, metal-roof re-screws, flashing, flat-roof repair and storm-damage repair in Citrus County, FL.",
          url: canonical("/roof-repair"),
          provider: { "@id": ROOFING_CONTRACTOR_ID },
          areaServed: { "@type": "AdministrativeArea", name: "Citrus County, Florida" },
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ]),
    }],
  }),
  component: Page,
});

function TrustRow() {
  const items = [
    "Free roof inspections",
    "Owner-performed inspections",
    "Same-day or next-day appointments often available",
    "Saturday emergency roof repair available",
    "Five-year workmanship warranty on most repairs",
    "Licensed and insured",
    `License #${" "}${BUSINESS.licenseNumber}`,
  ];
  return (
    <div className="not-prose bg-muted/50 border border-border rounded-lg p-4 my-6">
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {items.map((t) => (
          <li key={t} className="flex items-start gap-2">
            <span className="mt-1 size-1.5 rounded-full bg-cta shrink-0" aria-hidden />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RepairVsReplace() {
  return (
    <div className="not-prose grid md:grid-cols-2 gap-4 my-6">
      <div className="border-2 border-cta rounded-lg p-5 bg-card">
        <h3 className="font-display font-bold text-lg mb-3">Repair may make sense when:</h3>
        <ul className="space-y-1.5 text-sm">
          <li>· Damage is limited to one area</li>
          <li>· The surrounding roofing material remains serviceable</li>
          <li>· The source of the leak can be identified</li>
          <li>· The roof deck remains structurally sound</li>
          <li>· Flashing, a penetration, fastener, or small section has failed</li>
          <li>· The roofing system is not broadly deteriorated</li>
          <li>· A proper repair can reasonably restore the affected area</li>
        </ul>
      </div>
      <div className="border border-border rounded-lg p-5 bg-card">
        <h3 className="font-display font-bold text-lg mb-3">Replacement may be more practical when:</h3>
        <ul className="space-y-1.5 text-sm">
          <li>· Leaks repeatedly appear in different areas</li>
          <li>· Shingle granule loss is widespread</li>
          <li>· Fiberglass is exposed across the roof</li>
          <li>· Metal panels are rusting</li>
          <li>· Fastener and sealant failures appear throughout a metal roof</li>
          <li>· Decking damage is extensive</li>
          <li>· Flat-roof drainage failures are widespread</li>
          <li>· Previous repairs repeatedly fail</li>
          <li>· The repair would consume a substantial portion of replacement cost</li>
        </ul>
      </div>
    </div>
  );
}

function RepairCards() {
  const items = [
    "Active roof leaks",
    "Missing shingles",
    "Lifted or creased shingles",
    "Damaged ridge caps",
    "Granule-loss areas",
    "Failed pipe boots",
    "Electrical-riser boot leaks",
    "Wall and chimney flashing",
    "Skylight leaks",
    "Valley leaks",
    "Drip-edge leaks",
    "Off-ridge and ridge-vent problems",
    "Exposed-fastener metal roof leaks",
    "Backing-out metal screws",
    "Failed metal-roof washers",
    "Failed metal-roof sealant",
    "Separated TPO seams",
    "Flat-roof flashing failures",
    "Storm and wind damage",
    "Limited damaged decking",
    "Fascia and soffit deterioration",
  ];
  return (
    <div className="not-prose grid sm:grid-cols-2 md:grid-cols-3 gap-2 my-6">
      {items.map((t) => (
        <div key={t} className="border border-border rounded-md bg-card px-3 py-2 text-sm flex items-start gap-2">
          <span className="mt-1 size-1.5 rounded-full bg-cta shrink-0" aria-hidden />
          <span>{t}</span>
        </div>
      ))}
    </div>
  );
}

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Services", href: "/services" }, { label: "Roof Repair", href: "/roof-repair" }]} />
      <PageHero showRating
        eyebrow="Roof Repair"
        title="Roof Repair in Citrus County, FL"
        subtitle="A roof leak does not automatically mean you need a complete replacement. Destination Roofing Inc. provides roof inspections and repairs throughout Citrus County for shingle, metal, flat, and low-slope roofing systems. Owner and licensed roofing contractor Deston Judy generally performs the inspection personally, identifies the likely source of the problem, and explains whether a repair can reasonably restore the affected area."
      />
      <ServicePageBody title="Roof Repair" service="Roof Repair">
        <TrustRow />

        <div className="not-prose bg-cta/10 border border-cta/40 rounded-lg p-4 my-6">
          <p className="text-sm">
            <strong>Need urgent roof repair?</strong> Destination Roofing is open Saturdays and offers emergency and after-hours roof response when available, depending on weather, scheduling and roof safety. Call {BUSINESS.phone}.
          </p>
        </div>

        <h2>Emergency Roof Repair in Citrus County</h2>
        <p>
          Active roof leaks and storm damage sometimes cannot wait until Monday. Destination Roofing provides emergency roof repair and Saturday availability for urgent roofing problems when scheduling, weather and safe roof access allow. We inspect the actual problem and determine whether a temporary measure, permanent repair or roof replacement is appropriate.
        </p>

        <h2>What to Do When You Notice a Roof Leak</h2>
        <ul>
          <li>Move furniture and valuables away from the affected area.</li>
          <li>Place a container beneath active dripping water.</li>
          <li>Photograph visible ceiling stains and interior damage.</li>
          <li>Avoid climbing onto a wet or storm-damaged roof.</li>
          <li>Do not puncture a sagging ceiling unless directed by a qualified professional.</li>
          <li>Call for an inspection as soon as practical.</li>
          <li>If water is near electrical fixtures, avoid the affected area and contact an appropriate electrical professional when necessary.</li>
        </ul>
        <p>
          The location of an interior stain is not always directly below the exterior roof failure. Water can travel along decking, trusses, fasteners, underlayment, or other building components before becoming visible indoors.
        </p>

        <h2>Roof Problems We Inspect and Repair</h2>
        <RepairCards />
        <p>
          Not every roofing problem can be diagnosed from a photograph or a phone call. A proper inspection allows Destination Roofing to see what is actually happening on the roof and inside the attic when needed.
        </p>

        <h2>Finding the Source of a Roof Leak</h2>
        <ol>
          <li><strong>Speak with the homeowner.</strong> Deston asks where the leak appears, when it occurs, how long it has been present, and whether it becomes worse during wind-driven rain.</li>
          <li><strong>Inspect the interior when needed.</strong> He may inspect the ceiling, attic, or visible framing when the exterior source is not immediately clear.</li>
          <li><strong>Access the roof.</strong> Deston generally gets onto the roof unless weather, steep pitch, or unsafe conditions prevent access.</li>
          <li><strong>Inspect the likely failure points.</strong> Depending on the roof, he checks shingles or panels, valleys, flashing, pipe boots, electrical risers, chimneys, skylights, ridge and off-ridge vents, walls and transitions, fasteners and washers, sealant, low-slope areas, and signs of damaged decking.</li>
          <li><strong>Photograph the condition.</strong> Destination Roofing photographs roofs that can be safely accessed and can provide those images to the homeowner upon request.</li>
          <li><strong>Explain the recommendation.</strong> The homeowner receives an explanation of whether the problem appears isolated, repairable, or part of a larger <Link to="/roof-inspection">roofing-system failure</Link>.</li>
        </ol>

        <h2>When Does Roof Repair Make Sense?</h2>
        <p>
          Destination Roofing generally recommends repair when the roof system is still relatively serviceable and there is a clear, isolated reason for the failure.
        </p>
        <RepairVsReplace />
        <p>
          Destination Roofing has talked homeowners out of roof replacement when a proper repair could reasonably solve the problem. The recommendation is based on the condition of the actual roof, not on selling the most expensive option. If broader deterioration is present, we will explain why <Link to="/roof-replacement">roof replacement</Link> may be the more reliable choice.
        </p>

        <h2>Shingle Roof Repair</h2>
        <ul>
          <li>Missing or wind-damaged shingles</li>
          <li>Lifted or creased shingles</li>
          <li>Limited granule loss</li>
          <li>Failed ridge caps</li>
          <li>Pipe-boot leaks</li>
          <li>Wall flashing failures</li>
          <li>Valley leaks</li>
          <li>Chimney leaks</li>
          <li>Skylight leaks</li>
          <li>Drip-edge problems</li>
          <li>Small damaged-decking areas</li>
        </ul>
        <p>
          Repairs should integrate with the existing roofing system rather than simply covering the problem with exposed sealant. Destination Roofing primarily works with Atlas roofing systems but may repair other shingle brands when compatible materials and a reasonable repair method are available.
        </p>
        <p>
          Matching old shingle colors may not always be possible because of fading, discontinued products, and manufacturing changes. Existing flashing is evaluated for rust, holes, and structural condition — sound flashing may remain, and failed or severely corroded flashing should be replaced. Lead pipe boots are commonly used for plumbing penetrations. Electrical risers may require neoprene zipper boots. Chimneys wider than approximately 32 inches may require a cricket, depending on the roof and applicable requirements.
        </p>
        <p>Learn more about <Link to="/shingle-roofing">shingle roofing</Link>.</p>

        <h2>Metal Roof Repair and Re-Screw Services</h2>
        <ul>
          <li>Screws backing out</li>
          <li>Failed rubber washers</li>
          <li>Loose fasteners</li>
          <li>Failed sealant</li>
          <li>Leaks around penetrations</li>
          <li>Ridge, eave, gable, or transition leaks</li>
          <li>Damaged metal trim</li>
          <li>Limited panel damage</li>
          <li>Improper previous repair materials</li>
        </ul>
        <p>
          A leaking metal roof does not always need complete replacement. When the panels remain structurally sound and are not widely rusted, Destination Roofing may recommend a complete re-screw.
        </p>
        <ul>
          <li>Existing screws are removed.</li>
          <li>Wider-diameter long-life ZAC screws are installed so they can properly bite into the existing fastener openings.</li>
          <li>Wood-to-metal or metal-to-metal screws are selected according to the assembly.</li>
          <li>Penetrations and vulnerable details are resealed.</li>
          <li>Manufacturer-compatible, color-matched sealant is used where appropriate.</li>
          <li>The completed roof is inspected for missing or improperly installed fasteners.</li>
        </ul>
        <p>
          A re-screw is generally not recommended when the metal panels are widely rusted, severely deteriorated, or unable to reliably hold replacement fasteners. In those situations, we discuss <Link to="/metal-roofing">metal roofing</Link> replacement or another appropriate <Link to="/roof-replacement">roof-replacement</Link> option.
        </p>

        <h2>Flat and Low-Slope Roof Repair</h2>
        <p>Flat and low-slope systems may fail at:</p>
        <ul>
          <li>Seams</li>
          <li>Flashing</li>
          <li>Wall transitions</li>
          <li>Penetrations</li>
          <li>Drains</li>
          <li>Scuppers</li>
          <li>Edges</li>
          <li>Previous repair areas</li>
          <li>Ponding-water locations</li>
        </ul>
        <p>
          TPO seam separations can often be repaired when the surrounding membrane remains serviceable. Welded repairs should be properly prepared, welded, and tested. Drainage and ponding conditions must be considered before recommending a limited repair. Deston is cautious about recommending a simple repair when water remains on the roof for approximately 24 to 48 hours. Tapered insulation may be needed when a larger project requires positive drainage correction.
        </p>
        <p>
          Learn more about <Link to="/flat-roofing">flat roofing</Link> and <Link to="/commercial-roofing">commercial roofing</Link>.
        </p>
        <p>We also repair <Link to="/mobile-home-roofing">mobile and manufactured home roof leaks</Link> at seams, vents and flashing.</p>

        <h2>Small Roof Details That Commonly Cause Leaks</h2>
        <p>Many leaks occur at details rather than in the middle of the roof surface.</p>
        <h3>Pipe boots</h3>
        <p>Cracked, deteriorated, or improperly installed pipe boots can allow water to enter around plumbing penetrations.</p>
        <h3>Electrical risers</h3>
        <p>Electrical risers require properly fitted and sealed flashing or zipper-boot details.</p>
        <h3>Chimneys</h3>
        <p>Chimneys require properly integrated flashing. Wider chimneys may require a cricket to redirect water.</p>
        <h3>Skylights</h3>
        <p>Aging skylights can become leak points. Replacement may be more practical during a reroof, while isolated flashing repairs may be appropriate in some situations.</p>
        <h3>Walls and sidewalls</h3>
        <p>Step flashing, counterflashing, roofing cement, sealant, and siding transitions must work together correctly.</p>
        <h3>Ventilation</h3>
        <p>
          Ridge vents, Omni-style vents, off-ridge vents, and soffit intake must be evaluated as part of the overall roofing and attic system. Learn more about <Link to="/soffit">soffit</Link> work and our <Link to="/roof-inspection">roof-inspection</Link> process.
        </p>

        <h2>Storm and Wind-Damage Roof Repair</h2>
        <p>
          Storm damage is not always as obvious as a missing shingle. Strong winds can lift, crease, loosen, or break shingle seals without removing the entire shingle. Hail may leave impact marks, indentations, or dark areas where granules were knocked away.
        </p>
        <p>Destination Roofing can:</p>
        <ul>
          <li>Photograph visible roof conditions</li>
          <li>Document damaged roofing components</li>
          <li>Prepare a roofing repair or replacement estimate</li>
          <li>Meet an insurance adjuster when needed</li>
          <li>Provide scope revisions or supplements when appropriate</li>
          <li>Coordinate with properly licensed insurance professionals</li>
        </ul>
        <p>
          Insurance coverage depends on the cause of damage, policy language, exclusions, deductible, documentation, and the insurance company's decision. Destination Roofing does not guarantee claim approval or interpret insurance-policy coverage. Read more about <Link to="/storm-damage-roof-repair">storm-damage roof repair</Link>.
        </p>

        <h2>What Affects the Cost of Roof Repair?</h2>
        <p>Repair pricing depends on:</p>
        <ul>
          <li>Roof type</li>
          <li>Roof pitch</li>
          <li>Height</li>
          <li>Accessibility</li>
          <li>Size of damaged area</li>
          <li>Source of the leak</li>
          <li>Matching-material availability</li>
          <li>Flashing requirements</li>
          <li>Chimney or skylight details</li>
          <li>Electrical risers</li>
          <li>Damaged decking</li>
          <li>Fascia or <Link to="/soffit">soffit</Link> damage</li>
          <li>Metal-panel condition</li>
          <li>Fastener quantity</li>
          <li>Flat-roof membrane condition</li>
          <li>Drainage problems</li>
          <li>Previous repair materials</li>
          <li>Safety and equipment requirements</li>
        </ul>
        <p>
          A small interior stain does not always equal a small exterior repair. Water may travel before becoming visible, so a reliable estimate requires inspecting the actual roofing system. Gutter condition may also affect water behavior at the eave — see our <Link to="/gutters">gutter</Link> services.
        </p>

        <h2>Our Roof-Repair Process</h2>
        <ol>
          <li><strong>Initial contact.</strong> Liz or Deston receives the call or website request and creates a work order in AccuLynx. The company aims to respond promptly, often within about one hour.</li>
          <li><strong>Free inspection.</strong> Deston generally performs the inspection personally.</li>
          <li><strong>Diagnosis.</strong> The affected area, roof details, and interior conditions are evaluated as needed.</li>
          <li><strong>Recommendation.</strong> The homeowner is told whether the problem appears repairable or whether larger roof-system deterioration is present.</li>
          <li><strong>Written estimate.</strong> Destination Roofing commonly prepares and explains the estimate during the site visit.</li>
          <li><strong>Repair.</strong> The approved repair is completed using materials and methods appropriate for the roof system.</li>
          <li><strong>Final review.</strong> The repair, surrounding area, workmanship, and cleanup are reviewed. Final photographs are taken when appropriate.</li>
        </ol>

        <h2>Protecting Your Property During Roof Repair</h2>
        <ul>
          <li>Tarps may be used to protect landscaping.</li>
          <li>Plywood or barriers may be used to shield air-conditioning equipment.</li>
          <li>Newer or higher-quality driveways may receive additional protection.</li>
          <li>Debris is removed from the work area.</li>
          <li>Magnetic nail bars are used repeatedly when roofing fasteners are removed.</li>
          <li>Destination Roofing takes cleanup seriously even on smaller repair projects.</li>
        </ul>

        <h2>Five-Year Workmanship Warranty on Most Repairs</h2>
        <p>
          Destination Roofing generally provides a five-year workmanship warranty on the areas the company directly repairs. The warranty is intended to cover labor and material required to correct leaks caused by improper repair installation, subject to the written estimate, contract, exclusions, and warranty terms.
        </p>
        <p>
          The repair warranty does not mean Destination Roofing is responsible for an unrelated future leak in another roof area or deterioration outside the repaired scope.
        </p>
        <p>
          If a legitimate problem develops in an area Destination Roofing repaired, the company's approach is to inspect the issue and honor the written warranty rather than automatically search for a reason to avoid responsibility. Some roof-to-wall connections may also be evaluated for <Link to="/third-nail-wind-mitigation">third-nail wind-mitigation improvements</Link> during related work.
        </p>

        <h2>Roof Repair Experience Across Citrus County</h2>
        <figure className="my-6 rounded-xl overflow-hidden border border-border bg-white not-prose">
          <ResponsivePicture
            image={LECANTO_METAL_BEFORE}
            alt="Deteriorated metal roof with failed fasteners and sealant photographed during a roof inspection in Lecanto, Florida"
            sizes="(min-width: 1024px) 66vw, 100vw"
            imgClassName="w-full h-auto"
          />
          <figcaption className="p-3 text-sm text-muted-foreground">Existing deteriorated metal roof condition photographed during a Destination Roofing inspection in Lecanto, FL.</figcaption>
        </figure>
        <p>
          We handle roof repair in <Link to="/roofing-company-beverly-hills-fl">Beverly Hills</Link>, <Link to="/roofing-company-inverness-fl">Inverness</Link>, <Link to="/roofing-company-crystal-river-fl">Crystal River</Link>, <Link to="/roofing-company-homosassa-fl">Homosassa</Link>, <Link to="/roofing-company-lecanto-fl">Lecanto</Link>, <Link to="/roofing-company-hernando-fl">Hernando</Link>, <Link to="/roofing-company-floral-city-fl">Floral City</Link>, <Link to="/roofing-company-citrus-springs-fl">Citrus Springs</Link>, <Link to="/roofing-company-dunnellon-fl">Dunnellon</Link>, <Link to="/roofing-company-spring-hill-fl">Spring Hill</Link>, and surrounding communities. View additional finished work in our <Link to="/gallery">project gallery</Link> or browse all <Link to="/service-areas">service areas</Link> and <Link to="/services">services</Link>.
        </p>
        <p>Learn how <Link to="/florida-roof-insurance-rules">Florida&apos;s roof insurance rules work</Link> before you call your insurer.</p>
        <p>See our <Link to="/roof-cost-citrus-county">2026 Citrus County roof-cost guide</Link> for typical leak-repair pricing.</p>

        <h2>What Homeowners Say About Destination Roofing</h2>
        <div className="not-prose grid gap-4 md:grid-cols-3 my-6">
          {REVIEWS.map((r) => (
            <blockquote key={r.name} className="border border-border rounded-lg p-5 bg-card">
              <p className="text-sm leading-relaxed">"{r.text}"</p>
              <footer className="mt-3 text-sm font-semibold">— {r.name}</footer>
            </blockquote>
          ))}
        </div>
        <p><Link to="/reviews">Read More Customer Reviews</Link></p>

        <h2>Roof-Repair Guidance From an Experienced Local Roofer</h2>
        <p>
          This page is reviewed by Deston Judy, owner and licensed roofing contractor for Destination Roofing Inc. Deston began working in roofing at age 15 and now personally performs many of the company's roof inspections, diagnoses leaks, and helps homeowners determine whether repair or replacement is the more reasonable option.
        </p>
        <ul>
          <li>Florida roofing license #{" "}{BUSINESS.licenseNumber}</li>
          <li>More than 100 roofs completed by Destination Roofing</li>
          <li>Approximately 15 years rooted in Citrus County</li>
        </ul>
        <p><Link to="/about">More about Deston and Destination Roofing →</Link></p>

        <h2>Roof-Repair Questions</h2>
        <div className="not-prose space-y-3 my-6">
          {FAQS.map((f) => (
            <details key={f.q} className="border border-border rounded-lg bg-card">
              <summary className="cursor-pointer font-semibold px-4 py-3">{f.q}</summary>
              <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>

        <h2 id="request-inspection">Get an Honest Roof-Repair Recommendation</h2>
        <p>
          Destination Roofing will inspect the actual roof conditions and explain whether a repair can reasonably solve the problem or whether broader deterioration should be considered.
        </p>
        <div className="not-prose flex flex-col sm:flex-row gap-3 my-4">
          <Link to="/contact" className="btn-cta w-full sm:w-auto">
            <CalendarCheck className="size-5" /> Schedule My Free Inspection
          </Link>
          <a href={BUSINESS.phoneHref} className="inline-flex items-center justify-center gap-2 border-2 border-brand text-brand font-display font-bold uppercase tracking-wider rounded-md px-6 py-3 w-full sm:w-auto hover:bg-brand hover:text-brand-foreground transition-colors">
            <Phone className="size-5" /> Call {BUSINESS.phone}
          </a>
        </div>
      </ServicePageBody>
    </>
  );
}
